import { useLocation, useNavigate } from "react-router-dom";

import { NotEnoughSeats } from "features/subscriptions/components";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import { PaginatedDataTable } from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";
import useAvailableLicensesCount from "common/hooks/useAvailableLicensesCount";
import type { WorkspaceAccount } from "common/types";

import { ACCOUNTS_COLUMNS, MANAGE_ACCOUNTS_ACTIONS } from "../../constants";
import AccountActionsDropdown from "../AccountActionsDropdown";
import AccountName from "../AccountName";
import AccountProfileImage from "../AccountProfileImage";
import AssignSeats from "../ManageAccounts/AssignSeats";
import ManageInteractions from "../ManageAccounts/ManageInteractions";
import UpdateProxies from "../ManageAccounts/UpdateProxies";
import DesktopQuickAction from "./DesktopQuickAction";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";

interface DesktopTableProps {
  accounts: WorkspaceAccount[];
  selectedAccounts: WorkspaceAccount[];
  setSelectedAccounts: (selectedAccounts: WorkspaceAccount[]) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
}

const ACCOUNTS_DESKTOP_COLUMNS: ColumnDef<WorkspaceAccount>[] = [
  {
    id: "account",
    header: "Account",
    cell: ({ row: { original } }) => {
      const { occupation } = original;

      return (
        <div className="flex items-center gap-x-2.5">
          <AccountProfileImage account={original} />
          <div className="flex flex-col gap-y-1">
            <AccountName account={original} className="text-md-bold" />
            <span className="line-clamp-1 break-all text-xs-medium text-black-500">
              {occupation}
            </span>
          </div>
        </div>
      );
    },
    enableHiding: false,
  },
  ...typedObjectEntries(ACCOUNTS_COLUMNS).map(
    ([id, { label, icon, renderTag }]): ColumnDef<WorkspaceAccount> => ({
      id,
      header: label,
      cell: ({ row: { original } }) => renderTag(original, true),
      meta: {
        icon,
        className:
          id === "warmup" || id === "ssi"
            ? "min-w-28 max-w-40"
            : "min-w-40 max-w-40",
      },
    }),
  ),
  {
    id: "action",
    cell: ({ row: { original } }) => (
      <div className="flex gap-x-2.5 justify-end">
        <DesktopQuickAction account={original} />
        <AccountActionsDropdown account={original} />
      </div>
    ),
    enableHiding: false,
    meta: { className: "w-0", isSticky: true },
  },
];

export default function DesktopTable({
  accounts,
  selectedAccounts,
  setSelectedAccounts,
  columnVisibility,
  setColumnVisibility,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useSelectedWorkspaceContext();
  const { availableLicensesCount } = useAvailableLicensesCount();

  function onRowClick(account: WorkspaceAccount) {
    navigate({
      pathname: account.id,
      search: location.search,
    });
  }

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "accounts",
      selectedRows: selectedAccounts,
      onRowSelectionChange: permissions.includes("accounts_full")
        ? setSelectedAccounts
        : undefined,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
    reactTableOptions: { data: accounts, columns: ACCOUNTS_DESKTOP_COLUMNS },
    isLoading: false,
  });

  return (
    <PaginatedDataTable
      title="Accounts"
      table={table}
      onRowClick={onRowClick}
      isLoading={false}
      selectionActionsComponent={
        <BulkManageDesktop
          actions={MANAGE_ACCOUNTS_ACTIONS}
          selection={selectedAccounts}
          clearSelection={() => setSelectedAccounts([])}
          renderActionComponent={({ action, onClose }) => {
            const noLicenseAccounts = selectedAccounts.filter(
              ({ license, type }) => type === "user" && !license,
            );
            if (
              action === "seats" &&
              availableLicensesCount < noLicenseAccounts.length
            ) {
              return (
                <NotEnoughSeats
                  noSeatAccountIds={noLicenseAccounts.map(({ id }) => id)}
                  mixpanelSource="upsell_seat"
                />
              );
            }
            const props = {
              selectedAccounts,
              onClose: (shouldClearSelection: boolean) => {
                onClose();
                if (shouldClearSelection) {
                  setSelectedAccounts([]);
                }
              },
            };
            if (action === "proxy") {
              return <UpdateProxies {...props} />;
            }
            if (action === "interactions") {
              return <ManageInteractions {...props} />;
            }
            if (action === "seats") {
              return <AssignSeats {...props} />;
            }
          }}
        />
      }
    />
  );
}
